import React, { useEffect, useMemo, useState } from 'react';
import { Member, SplitShares } from 'types/splits.types';
import { useContributionRolesDDL } from 'app/utils/hooks';
import { useFieldArray, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { sumMembersShares } from 'app/components/Song/Splits/utils';
import { OverlayDialog, OverlayHeader } from 'app/components/Dialogs';
import { ContentContainer } from 'app/components/Core/ChakraContentContainer';
import { ContentContainerType } from 'types/enums';
import { NoMembersMessage } from 'app/components/Song/Splits/NoMembersMessage';
import { ProposeSplitList } from 'app/components/Song/Splits/ProposeSplitList';
import { ProposeSplitActions } from 'app/components/Song/Splits/ProposeSplitActions';
import { chakra } from '@chakra-ui/react';
import * as yup from 'yup';
import {
  getDefaultSplitMembers,
  getMembersValues,
  sortSplitMembers,
} from './utils';

interface NewSplitOverlayComponentProps {
  onClose: () => void;
  isLoading: boolean;
  possibleMembers?: Member[];
  onSubmit?: (values: SplitShares) => Promise<void>;
  currentMembers?: Member[];
}

const Form = chakra('form', {
  baseStyle: {
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '100%',
    minHeight: '100px',
  },
});

const validationSchema = yup.object().shape({
  members: yup.array().of(
    yup.object({
      share: yup
        .string()
        .transform((value) => value.replace('%', ''))
        .min(0, '')
        .max(100, '')
        .typeError(''),
    })
  ),
});

export const NewSplitOverlayComponent: React.FC<NewSplitOverlayComponentProps> = ({
  onClose,
  isLoading,
  possibleMembers,
  onSubmit = () => {},
  currentMembers,
}) => {
  const [splitMembers, setSplitMembers] = useState<Member[]>(
    getDefaultSplitMembers(possibleMembers, currentMembers)
  );
  const contributionRolesDDL = useContributionRolesDDL();

  useEffect(() => {
    setSplitMembers(getDefaultSplitMembers(possibleMembers, currentMembers));
  }, [currentMembers, possibleMembers]);

  const {
    control,
    setValue,
    watch,
    getValues,
    handleSubmit,
    reset,
    trigger,
    formState: { isSubmitting, isValid },
  } = useForm<SplitShares>({
    defaultValues: {
      members: getMembersValues([], splitMembers, currentMembers || []),
    },
    mode: 'all',
    resolver: yupResolver(validationSchema),
  });
  useFieldArray({ control, name: 'members' });

  const watchEntries = watch('members');
  const entriesSum = sumMembersShares(watchEntries);

  const onMemberSelect = (member: Member) => {
    const sortedSplitMembers = sortSplitMembers(
      [...splitMembers, member],
      possibleMembers
    );
    setSplitMembers(sortedSplitMembers);
  };

  const onMemberRemove = (member: Member) => {
    const sortedSplitMembers = splitMembers.filter(
      ({ memberIdentifier }) => member.memberIdentifier !== memberIdentifier
    );
    setSplitMembers(sortedSplitMembers);
  };

  useEffect(() => {
    const previousValues = getValues('members');
    const membersValues = getMembersValues(previousValues, splitMembers, []);
    reset({ members: membersValues });
  }, [reset, trigger, getValues, splitMembers, currentMembers]);

  const membersSelectionList = useMemo(
    () =>
      possibleMembers?.filter(
        (possibleMember) =>
          !splitMembers.some(
            ({ memberIdentifier }) =>
              possibleMember.memberIdentifier === memberIdentifier
          )
      ),
    [possibleMembers, splitMembers]
  );

  return (
    <OverlayDialog onClose={onClose}>
      <ContentContainer
        type={ContentContainerType.NARROW_LIST}
        position="relative"
        height="full"
      >
        <Form onSubmit={handleSubmit(onSubmit)}>
          <OverlayHeader headerText="Propose new Split" />
          {!isLoading && !possibleMembers?.length ? (
            <NoMembersMessage onButtonClick={onClose} />
          ) : (
            <>
              <ProposeSplitList
                splitMembers={splitMembers}
                membersSelectionList={membersSelectionList}
                getValues={getValues}
                setValue={setValue}
                trigger={trigger}
                control={control}
                entriesSum={entriesSum}
                contributionRolesDDL={contributionRolesDDL}
                onMemberSelect={onMemberSelect}
                removeMember={onMemberRemove}
                isLoading={isLoading}
              />
              <ProposeSplitActions
                marginTop={10}
                onMemberSelect={onMemberSelect}
                onClose={onClose}
                confirmEnabled={
                  splitMembers?.length > 0 &&
                  !isSubmitting &&
                  isValid &&
                  +entriesSum <= 100 &&
                  +entriesSum > 0
                }
              />
            </>
          )}
        </Form>
      </ContentContainer>
    </OverlayDialog>
  );
};
